import SearchInput from "@dashboard/components/AppLayout/ListFilters/components/SearchInput";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import { Pill } from "@dashboard/components/Pill";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TablePagination, { ListSettingsUpdate } from "@dashboard/components/TablePagination";
import TableRowLink from "@dashboard/components/TableRowLink";
import { WsmDealerCustomerFragment } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { getFullName, renderCollection } from "@dashboard/misc";
import { ListSettings } from "@dashboard/types";
import { TableBody, TableCell, TableFooter, TableHead } from "@material-ui/core";
import { Box, Button, Skeleton, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { customerMessages, railMessages, sectionMessages } from "../../messages";
import { wsmDealerCustomerAddPath, wsmDealerCustomerUrl } from "../../urls";
import { getGroupLabel } from "../../utils";
import WsmDealerTabs from "../WsmDealerTabs/WsmDealerTabs";

const numberOfColumns = 4;

export interface WsmDealerCustomerListPageProps {
  customers: WsmDealerCustomerFragment[] | undefined;
  query: string;
  settings: ListSettings;
  /** Set when the merchant arrived from one group's rail. */
  groupLabel: string | null;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onQueryChange: (query: string) => void;
  onUpdateListSettings: ListSettingsUpdate;
  onNextPage: () => void;
  onPreviousPage: () => void;
}

export const WsmDealerCustomerListPage = ({
  customers,
  query,
  settings,
  groupLabel,
  hasNextPage,
  hasPreviousPage,
  onQueryChange,
  onUpdateListSettings,
  onNextPage,
  onPreviousPage,
}: WsmDealerCustomerListPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();

  return (
    <ListPageLayout>
      <TopNav title={intl.formatMessage(sectionMessages.customerListTitle)}>
        <Button
          onClick={() => navigate(wsmDealerCustomerAddPath)}
          data-test-id="wsm-dealer-customer-assign"
        >
          {intl.formatMessage(customerMessages.assignCustomer)}
        </Button>
      </TopNav>
      <Box overflowY="auto" height="100%">
        <WsmDealerTabs current="customers" />
        <Box paddingTop={5}>
          <DashboardCard data-test-id="wsm-dealer-customer-list">
            <DashboardCard.Content paddingX={0}>
              <Box paddingTop={4} paddingBottom={5} display="grid" gap={3}>
                <SearchInput
                  initialSearch={query}
                  placeholder={intl.formatMessage(customerMessages.searchPlaceholder)}
                  onSearchChange={onQueryChange}
                />
                {groupLabel && (
                  <Box paddingX={6}>
                    <Text size={2} color="default2" data-test-id="wsm-dealer-customer-group-filter">
                      {intl.formatMessage(railMessages.filteredByGroup, { group: groupLabel })}
                    </Text>
                  </Box>
                )}
              </Box>
              <ResponsiveTable>
                <TableHead>
                  <TableRowLink>
                    <TableCell>{intl.formatMessage(customerMessages.colCustomer)}</TableCell>
                    <TableCell>{intl.formatMessage(customerMessages.colEmail)}</TableCell>
                    <TableCell>{intl.formatMessage(customerMessages.colGroup)}</TableCell>
                    <TableCell>{intl.formatMessage(customerMessages.colTaxExempt)}</TableCell>
                  </TableRowLink>
                </TableHead>
                <TableBody>
                  {renderCollection(
                    customers,
                    customer => (
                      <TableRowLink
                        key={customer ? customer.id : "skeleton"}
                        hover={!!customer}
                        href={customer ? wsmDealerCustomerUrl(customer.id) : undefined}
                        data-test-id="wsm-dealer-customer-row"
                      >
                        <TableCell>
                          {customer ? getFullName(customer.user) || "-" : <Skeleton />}
                        </TableCell>
                        <TableCell>
                          {customer ? (
                            // An email has no spaces to wrap on, so at 1024 it
                            // ran straight over the next column. Clamped, with
                            // the whole address on hover.
                            <Box
                              __maxWidth="220px"
                              __overflow="hidden"
                              __textOverflow="ellipsis"
                              __whiteSpace="nowrap"
                              title={customer.user.email}
                            >
                              {customer.user.email}
                            </Box>
                          ) : (
                            <Skeleton />
                          )}
                        </TableCell>
                        <TableCell>
                          {customer ? getGroupLabel(customer.group) : <Skeleton />}
                        </TableCell>
                        <TableCell>
                          {customer ? (
                            <Pill
                              color={customer.taxExempt ? "success" : "generic"}
                              label={intl.formatMessage(
                                customer.taxExempt
                                  ? customerMessages.taxExemptYes
                                  : customerMessages.taxExemptNo,
                              )}
                              data-test-id="wsm-dealer-customer-tax-pill"
                            />
                          ) : (
                            <Skeleton />
                          )}
                        </TableCell>
                      </TableRowLink>
                    ),
                    () => (
                      <TableRowLink data-test-id="wsm-dealer-customer-list-empty">
                        <TableCell colSpan={numberOfColumns}>
                          <Box paddingY={6}>{intl.formatMessage(customerMessages.noCustomers)}</Box>
                        </TableCell>
                      </TableRowLink>
                    ),
                  )}
                </TableBody>
                <TableFooter>
                  <TableRowLink>
                    <TablePagination
                      colSpan={numberOfColumns}
                      settings={settings}
                      hasNextPage={hasNextPage}
                      hasPreviousPage={hasPreviousPage}
                      onUpdateListSettings={onUpdateListSettings}
                      onNextPage={onNextPage}
                      onPreviousPage={onPreviousPage}
                    />
                  </TableRowLink>
                </TableFooter>
              </ResponsiveTable>
            </DashboardCard.Content>
          </DashboardCard>
        </Box>
      </Box>
    </ListPageLayout>
  );
};

export default WsmDealerCustomerListPage;
